import Vue from 'vue'

class VerificationManager {
    constructor() {
        this.pendingRequests = new Map()
        this.verificationInProgress = false
        this.currentVerificationPromise = null
    }

    /**
     * Handle verification requirement from interceptor
     */
    async handleVerificationRequired(error) {
        const requestId = this.generateRequestId()
        const response = error.response.data
        const verificationData = response.verification || {}
        const availableMethods = response.methods || []
        const endpoints = response.endpoints || {}

        console.log('handleVerificationRequired', { verificationData, endpoints })

        // Store the original request
        this.pendingRequests.set(requestId, {
            config: error.config,
            timestamp: Date.now(),
            verificationType: verificationData.type
        })

        try {
            // If verification is already in progress, wait for it
            if (this.verificationInProgress && this.currentVerificationPromise) {
                await this.currentVerificationPromise
            } else {
                // Start new verification flow
                await this.startVerification(verificationData, endpoints, availableMethods)
            }

            // Retry the original request after successful verification
            return await this.retryRequest(requestId)
        } catch (verificationError) {
            // Clean up on failure
            this.pendingRequests.delete(requestId)
            throw verificationError
        }
    }

    /**
     * Start the verification flow
     */
    async startVerification(verificationData, endpoints = {}, availableMethods = {}) {
        if (this.verificationInProgress) {
            return this.currentVerificationPromise
        }

        this.verificationInProgress = true

        this.currentVerificationPromise = new Promise((resolve, reject) => {
            // New Vue instance to create the popup
            const vm = new Vue()

            vm.$popup('VerificationCodeInput', {
                verificationType: verificationData.type,
                methods: availableMethods || [],
                context: verificationData.context || {},
                title: verificationData.title,
                retryAfter: verificationData.retry_after || 60,
                endpoints: endpoints,
                // Pass async handlers
                onVerified: (data) => {
                    console.log('onVerified called within VerificationManager', data)

                    this.handleVerificationSuccess(data, resolve)
                },
                onCancel: () => {
                    console.log('onCancel called within VerificationManager')

                    this.handleVerificationCancel(reject)
                }
            })

            // Clean up the Vue instance
            vm.$destroy()
        })

        return this.currentVerificationPromise
    }

    /**
     * Handle successful verification
     */
    handleVerificationSuccess(data, resolve) {
        this.verificationInProgress = false

        resolve(data)
    }

    /**
     * Handle verification cancellation
     */
    handleVerificationCancel(reject) {
        this.verificationInProgress = false

        reject(new Error('Verification cancelled by user'))
    }

    /**
     * Retry the original request
     */
    async retryRequest(requestId) {
        const pendingRequest = this.pendingRequests.get(requestId)

        if (!pendingRequest) {
            throw new Error('Request not found')
        }

        // Clean up
        this.pendingRequests.delete(requestId)

        // Retry the request
        return await window.axios.request(pendingRequest.config)
    }

    /**
     * Generate unique request ID
     */
    generateRequestId() {
        return crypto.randomUUID();
    }
}

export default new VerificationManager()
